// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import baseTheme, { BaseTheme } from "@insite/mobius/globals/baseTheme";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { color, xScreen } from "@pacific/XDL/Utilities";
import styled, { css } from "styled-components";

export const FooterContainerWrapper = styled.div<any>`
    background: ${color("common.ui9")};
    color: ${color("common.ui1")};
    ${({ isCheckoutPage }: any) => {
        if (isCheckoutPage) {
            return css`
                background: ${color("common.ui6")};
            `;
        }
    }}
`;

export const FooterContentWrapper = styled.div`
    flex-grow: 1;
    width: 100%;
    margin: 0 auto;
    ${({ theme }: { theme: BaseTheme }) => {
        const { maxWidths } = theme.breakpoints || baseTheme.breakpoints;
        return breakpointMediaQueries(
            theme,
            maxWidths.map(mw =>
                mw
                    ? css`
                          max-width: ${mw}px;
                      `
                    : null,
            ),
        );
    }}

    @media print {
        max-width: 100%;
    }
`;

export const FooterTopSection = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 40px 34px 42px;
    flex-wrap: nowrap;
    ${xScreen("lte", "md")} {
        flex-direction: column;
        padding: 40px 31px 20px;
    }
    ${xScreen("lte", "sm")} {
        padding: 40px 16px 0;
    }
`;

export const FooterBottomSection = styled.div<any>`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    padding: 21px 34px;
    flex-wrap: wrap;
    ${xScreen("lte", "md")} {
        padding: 20px 31px;
    }
    ${xScreen("lte", "sm")} {
        padding: 20px 16px;
        flex-direction: column;
    }
    ${({ isCheckoutPage }: any) => {
        if (isCheckoutPage) {
            return css`
                &&& {
                    padding: 16px 34px;
                    ${xScreen("lte", "sm")} {
                        padding: 16px;
                    }
                }
            `;
        }
    }}
`;

export const FooterRightSection = styled.div`
    display: flex;
    flex-wrap: wrap;
    ${xScreen("lte", "md")} {
        order: -1;
    }
`;

export const FooterLeftSection = styled.div`
    display: flex;
    flex-direction: column;
    margin-right: 16px;
    max-width: 305px;
    ${xScreen("lte", "md")} {
        max-width: none;
        width: 50%;
    }
    ${xScreen("lte", "sm")} {
        width: 100%;
    }
    .logo-wrapper {
        margin-bottom: 24px;
        div.wrapper {
            max-height: 44px;
            max-width: 169px;
            .home-page-link {
                width: 169px;
                height: 44px;
                & span {
                    width: 100%;
                    height: 100%;
                }
            }
        }

        img {
            height: 100%;
            width: 100%;
        }
    }
    .support-services-wrapper {
        margin-bottom: 26px;
    }
`;

export const FooterSiteMapLinksWrapper = styled.div<any>`
    > div[class^="Wrapper"] {
        display: flex;
        align-items: center;
        line-height: 15px;
        > div:not(:last-child) {
            margin-right: 33px;
        }
        .link > span {
            color: ${color("common.ui1")};
            font-size: 10px;
            letter-spacing: 0;
            line-height: 15px;
            font-weight: 500;
            &:hover {
                color: ${color("common.ui8")};
            }
        }
        ${({ isCheckoutPage }: any) => {
            if (isCheckoutPage) {
                return css`
                    ${xScreen("lte", "sm")} {
                        width: 100%;
                        justify-content: space-between;
                    }
                `;
            }
        }}
    }
    ${xScreen("lte", "sm")} {
        width: 100%;
    }
`;

export const GeneralLinksWrapper = styled.div`
    max-width: 200px;
    min-width: 180px;
    &:not(:last-child) {
        margin-right: 22px;
    }
    ${xScreen("lte", "md")} {
        &&& {
            width: calc(25% - 14px);
            margin-right: 14px;
            margin-bottom: 40px;
            &:last-child {
                width: 25%;
                margin-right: 0;
            }
        }
    }
    ${xScreen("lte", "sm")} {
        &&&& {
            max-width: none;
            margin-bottom: 20px;
            width: calc(50% - 12px);
            &:nth-child(odd) {
                margin-right: 12px;
            }
            &:nth-child(even) {
                margin-left: 12px;
                margin-right: 0;
            }
        }
    }
    .list-wrapper {
        padding: 0;
        .title-wrapper .list-title > span {
            color: ${color("common.ui1")};
            &:hover {
                color: ${color("common.ui8")};
            }
        }
        .links-wrapper {
            margin-bottom: 8px;
            ${xScreen("lte", "md")} {
                padding-right: 0;
                margin-bottom: 0;
            }
            .list-link {
                > span {
                    color: ${color("common.ui1")};
                    font-size: 12px;
                    font-weight: 500;
                    line-height: 18px;
                    text-transform: capitalize;
                }
                &:hover > span {
                    color: ${color("common.ui8")};
                }
            }
        }
    }
`;

export const CopyRightWrapper = styled.div`
    font-size: 10px;
    letter-spacing: 0;
    line-height: 15px;
    margin-right: 20px;
    font-weight: 500;
    ${xScreen("lte", "sm")} {
        margin-bottom: 20px;
    }
`;
